import {
  BulkMessageCampaignTypeEnum,
  BulkMessageSendStatusEnum,
  User,
  BulkMessageSenderTypeEnum,
  BulkMessage,
  BulkMessageSentFromFilterInput,
} from "@/__generated__/types";
import { DataTable } from "@/components/DataTable";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, Share } from "lucide-react";
import { EmptyState } from "@/components/DataTable/States/Empty";
import {
  DEFAULT_INITIAL_RECORDS_BULK_MESSAGE,
  useBulkMessages,
} from "@/hooks/useBulkMessages";
import { useOrg } from "@/hooks/useOrg";
import {
  BulkMessageColumnsId,
  useBulkMessageTableConfig,
} from "@/hooks/useBulkMessageTableConfig";
import { ColumnDef } from "@tanstack/react-table";
import { useCounselors } from "@/hooks/useCounselors";
import { CustomFilterTable } from "@/components/DataTable/Header/Filter";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { useShareLink } from "@/hooks/useShareLink";

interface BulkMessageTableProps {
  sendStatus: BulkMessageSendStatusEnum;
}

interface ColumnMeta {
  label: string;
  avatarFilterLabel?: boolean;
  valueComponent?: React.ComponentType<{
    value: string;
    user?: User;
    senderType?: BulkMessageSenderTypeEnum;
  }>;
}

type BulkMessageColumnDef = ColumnDef<BulkMessage | null> & {
  meta: ColumnMeta;
  accessorFn?: (row: BulkMessage | null) => string;
};

const getPageCount = (totalCount: number) => {
  return Math.ceil(totalCount / DEFAULT_INITIAL_RECORDS_BULK_MESSAGE);
};

const filtersMapI18nKey: Record<BulkMessageCampaignTypeEnum, string> = {
  [BulkMessageCampaignTypeEnum.OneTimeSend]:
    "bulkMessaging.filters.oneTimeSend",
  [BulkMessageCampaignTypeEnum.Campaign]: "bulkMessaging.filters.campaign",
};

export function BulkMessageTableBase({
  sendStatus = BulkMessageSendStatusEnum.Sent,
}: BulkMessageTableProps) {
  const { t } = useTranslation();
  const { navToRoute } = useProtectedNavigation();
  const { shareCurrentLink } = useShareLink();
  const { org } = useOrg();
  const [pageIndex, setPageIndex] = useState(0);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [selectedFilterValues, setSelectedFilterValues] = useState<
    Record<string, string>
  >({});
  const {
    columns: overviewMessagesColumnsConfig,
    ordering,
    columnsConfigBasedOnSendStatus,
  } = useBulkMessageTableConfig(sendStatus);
  const { data: counselors } = useCounselors();

  // Add state for filters
  const [selectedFilters, setSelectedFilters] = useState<{
    sentFrom?: BulkMessageSentFromFilterInput[];
    sendStatus?: BulkMessageSendStatusEnum[];
    campaignType?: BulkMessageCampaignTypeEnum[];
  }>({});

  const {
    messagesData: sentMessages,
    loading: sentLoading,
    error,
    hasNextPage,
    hasPreviousPage,
    endCursor,
    startCursor,
    totalCount,
  } = useBulkMessages({
    orgId: org?.id,
    sendStatus,
    first:
      after || (!after && !before)
        ? DEFAULT_INITIAL_RECORDS_BULK_MESSAGE
        : undefined,
    last: before ? DEFAULT_INITIAL_RECORDS_BULK_MESSAGE : undefined,
    campaignType:
      selectedFilters.campaignType ||
      columnsConfigBasedOnSendStatus.bulkMessageType,
    ordering,
    after,
    before,
    sentFrom: selectedFilters.sentFrom,
  });

  /**
   * Handles filter changes for the bulk message table
   * @param filterId - The ID of the filter being changed
   * @param values - Array of selected values for the filter
   */
  const handleFilterChange = (filterId: string, values: string[]): void => {
    setPageIndex(0);
    setAfter(null);
    setBefore(null);
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };
      const [type] = values;

      switch (filterId) {
        case BulkMessageColumnsId.sentFrom:
          setSelectedFilterValues((prev) => ({
            ...prev,
            [filterId]: values[0],
          }));
          newFilters.sentFrom = values.map((value) => ({
            senderType: BulkMessageSenderTypeEnum.UserId,
            userIds: [value],
          }));
          break;

        case BulkMessageColumnsId.sendStatus:
          newFilters.sendStatus = values as BulkMessageSendStatusEnum[];
          break;

        case BulkMessageColumnsId.campaignType:
          newFilters.campaignType = [
            BulkMessageCampaignTypeEnum[
              type?.replaceAll(
                " ",
                "",
              ) as keyof typeof BulkMessageCampaignTypeEnum
            ],
          ];
          break;
      }

      return newFilters;
    });
  };

  /**
   * Gets the display value for the current filter
   * @returns The formatted display value or undefined if no value is selected
   */
  const getFilterDisplayValue = (): string | undefined => {
    const selectedValue = selectedFilterValues.sentFrom;
    if (!selectedValue) return undefined;

    const counselor = counselors?.find((c) => c?.id === selectedValue);
    if (counselor) {
      return `${counselor.firstName} ${counselor.lastName}`;
    }

    return selectedValue;
  };

  const columnsToFilterBy = [
    BulkMessageColumnsId.sentFrom,
    BulkMessageColumnsId.campaignType,
  ];

  const createSentFromFilter = (
    column: BulkMessageColumnDef,
  ): CustomFilterTable => ({
    id: column.id as string,
    label: column.meta.label,
    values:
      counselors
        ?.filter((counselor): counselor is User => counselor !== null)
        .map((counselor: User) => ({
          value: counselor.id,
          user: counselor,
          senderType: BulkMessageSenderTypeEnum.UserId,
        })) ?? [],
    avatarFilterLabel: Boolean(column.meta.avatarFilterLabel),
  });

  const createCampaignTypeFilter = (
    column: BulkMessageColumnDef,
  ): CustomFilterTable => ({
    id: column.id as string,
    label: column.meta.label,
    values: Object.values(BulkMessageCampaignTypeEnum).map((type) => ({
      value: t(filtersMapI18nKey[type]),
    })),
    avatarFilterLabel: false,
  });

  const customFilters: CustomFilterTable[] = overviewMessagesColumnsConfig
    .filter((column): column is BulkMessageColumnDef =>
      Boolean(
        column.enableColumnFilter &&
          column.meta &&
          "label" in column.meta &&
          columnsToFilterBy.includes(column.id as string),
      ),
    )
    .map((column: BulkMessageColumnDef): CustomFilterTable => {
      const columnId = column.id as string;
      return columnId === BulkMessageColumnsId.sentFrom
        ? createSentFromFilter(column)
        : createCampaignTypeFilter(column);
    });

  return (
    <div>
      <DataTable
        data={sentMessages}
        columns={[...overviewMessagesColumnsConfig]}
        isLoading={sentLoading}
        pagination={
          <>
            <DataTable.PaginationLabel
              pageCount={getPageCount(totalCount)}
              pageIndex={pageIndex}
            />
            <DataTable.PaginationControls
              onNextPage={
                hasNextPage &&
                (() => {
                  setBefore(null);
                  setAfter(endCursor || null);
                  setPageIndex(pageIndex + 1);
                })
              }
              onPreviousPage={
                hasPreviousPage &&
                (() => {
                  setAfter(null);
                  setBefore(startCursor || null);
                  setPageIndex(pageIndex - 1);
                })
              }
            />
          </>
        }
        tableHeader={<DataTable.THeader />}
        tableBody={<DataTable.TBody />}
        emptyState={
          error ? (
            <EmptyState
              copy={t("bulkMessaging.table.emptyStates.error")}
              icon={
                <CalendarClock
                  className="h-[36px] w-[36px] text-primary-light"
                  aria-label={t("bulkMessaging.table.emptyStates.error")}
                />
              }
            />
          ) : (
            <EmptyState
              copy={t(columnsConfigBasedOnSendStatus.emptyStateLabel)}
              buttonCopy={t("bulkMessaging.table.createNewBulkMessage")}
              buttonTarget={() => navToRoute("createBulkMessage")}
              icon={
                <CalendarClock
                  className="h-[36px] w-[36px] text-primary-light"
                  aria-label={t("bulkMessaging.table.createNewBulkMessage")}
                />
              }
            />
          )
        }
        topHeader={
          <>
            <DataTable.Title
              title={t(columnsConfigBasedOnSendStatus.title)}
              description={t(columnsConfigBasedOnSendStatus.description)}
            />
            <div className="flex items-center gap-2">
              <DataTable.Filter
                customFilters={customFilters}
                onFilterChange={handleFilterChange}
                displayValue={getFilterDisplayValue()}
              />
              <DataTable.HeaderActions
                className="h-8"
                actions={[
                  // Hide for now
                  // {
                  //   key: "export-csv",
                  //   render: () => (
                  //     <>
                  //       <FileDown
                  //         className="mr-2 h-4 w-4"
                  //         aria-label={t(
                  //           "bulkMessaging.table.actions.exportCsv",
                  //         )}
                  //       />
                  //       {t("bulkMessaging.table.actions.exportCsv")}
                  //     </>
                  //   ),
                  //   onClick: () => console.log("export-csv"),
                  // },
                  {
                    key: "share",
                    render: () => (
                      <>
                        <Share
                          className="mr-2 h-4 w-4"
                          aria-label={t("bulkMessaging.table.actions.share")}
                        />
                        {t("bulkMessaging.table.actions.share")}
                      </>
                    ),
                    onClick: shareCurrentLink,
                  },
                ]}
              />
            </div>
          </>
        }
      />
    </div>
  );
}
